<script setup>
import { defineProps, computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import router from '@/router'
import { useTheme } from '@/composables/useTheme'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import wordmarkWhite from '@/assets/brand/lockerbot-wordmark-white.svg'

const props = defineProps({
  userRole: { required: true },
  userName: { required: true },
  userEmail: { required: true },
})

const store = useStore()
const route = useRoute()
const { currentTheme, toggleTheme } = useTheme()

// Single source of navigation: an item appears on every page automatically.
// roles not set — the item is visible to all roles.
const MENU = [
  {
    section: null,
    items: [
      { label: 'Dashboard', icon: 'view-dashboard-outline', to: '/dashboard' },
    ],
  },
  {
    section: 'Operations',
    items: [
      { label: 'Lockers', icon: 'locker-multiple', to: '/lockers' },
      { label: 'Rental Orders', icon: 'clipboard-text-clock-outline', to: '/orders' },
      { label: 'Payments', icon: 'credit-card-outline', to: '/payments', roles: ['administrator'] },
      { label: 'Maintenance', icon: 'wrench-outline', to: '/maintenance', roles: ['administrator', 'manager'] },
      { label: 'System logs', icon: 'text-box-search-outline', to: '/devices-logs', roles: ['administrator', 'manager'] },
    ],
  },
  {
    section: 'Directory',
    items: [
      { label: 'Organizations', icon: 'office-building-outline', to: '/organizations', roles: ['administrator'] },
      { label: 'Locations', icon: 'map-marker-radius-outline', to: '/locations', roles: ['administrator', 'manager'] },
      { label: 'Device Models', icon: 'shape-outline', to: '/device-models', roles: ['administrator'] },
    ],
  },
  {
    section: 'Hardware',
    items: [
      { label: 'Control Units', icon: 'memory', to: '/control-units', roles: ['administrator'] },
      { label: 'Firmwares', icon: 'chip', to: '/firmwares', roles: ['administrator'] },
    ],
  },
]

const visibleMenu = computed(() =>
  MENU
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !item.roles || item.roles.includes(props.userRole)),
    }))
    .filter((group) => group.items.length > 0)
)

function isActive(item) {
  return route.path === item.to || route.path.startsWith(item.to + '/')
}

const initials = computed(() => {
  const name = (props.userName || '').trim()
  if (name) {
    return name
      .split(/\s+/)
      .map((w) => w[0])
      .slice(0, 2)
      .join('')
      .toUpperCase()
  }
  return (props.userEmail || 'LB').slice(0, 2).toUpperCase()
})

// user dropdown
const userMenuOpen = ref(false)
const userRef = ref(null)
function onDocClick(e) {
  if (userMenuOpen.value && userRef.value && !userRef.value.contains(e.target)) userMenuOpen.value = false
}
onMounted(() => document.addEventListener('mousedown', onDocClick))
onBeforeUnmount(() => document.removeEventListener('mousedown', onDocClick))

// sign out
const confirmSignOut = ref(false)
const logout = (val) => store.dispatch('auth/logout', val)
function doSignOut() {
  confirmSignOut.value = false
  logout().finally(() => router.push({ name: 'login' }))
}
</script>

<template>
  <aside class="sidebar">
    <div class="sidebar__brand">
      <router-link to="/dashboard">
        <img :src="wordmarkWhite" alt="Lockerbot">
      </router-link>
      <span class="tag">Admin</span>
    </div>

    <nav class="nav">
      <template v-for="(group, gi) in visibleMenu" :key="gi">
        <div v-if="group.section" class="nav__section">{{ group.section }}</div>
        <router-link
          v-for="item in group.items"
          :key="item.to"
          class="nav__item"
          :class="{ 'is-active': isActive(item) }"
          :to="item.to">
          <span class="mdi" :class="`mdi-${item.icon}`"></span>
          {{ item.label }}
        </router-link>
      </template>
    </nav>

    <div class="sidebar__foot">
      <div ref="userRef" class="sidebar__user">
        <button
          class="sidebar__user-btn"
          type="button"
          :aria-expanded="userMenuOpen"
          @click="userMenuOpen = !userMenuOpen">
          <div class="avatar">{{ initials }}</div>
          <div class="meta">
            <b>{{ userName }}</b>
            <span>{{ userEmail }}</span>
          </div>
          <span class="mdi mdi-chevron-up sidebar__user-caret"></span>
        </button>
        <div v-if="userMenuOpen" class="usermenu">
          <button class="usermenu__item" type="button" @click="toggleTheme">
            <span class="mdi" :class="currentTheme === 'dark' ? 'mdi-white-balance-sunny' : 'mdi-weather-night'"></span>
            {{ currentTheme === 'dark' ? 'Light theme' : 'Dark theme' }}
          </button>
          <button class="usermenu__item" type="button" @click="userMenuOpen = false; confirmSignOut = true">
            <span class="mdi mdi-logout"></span>
            Sign out
          </button>
        </div>
      </div>
    </div>

    <ConfirmModal
      :open="confirmSignOut"
      title="Sign out?"
      message="You’ll need to sign in again to access the admin panel."
      confirm-label="Sign out"
      icon="logout"
      @confirm="doSignOut"
      @cancel="confirmSignOut = false" />
  </aside>
</template>
